import { keysCamelToSnake } from 'js-utils/transformation';
import { HAS_WINDOW } from 'eventbrite_design_system/utils/dom';
import {
    setLocationData,
    getBrowserLocation,
    transformGeoPlaceObject,
    location as locationDuck,
    parseLocationResponse,
    storeQuery,
    constants as destinationConstants,
} from '@eb/destination-utils';

import { updateSearchLocationSlug } from '../search/actions';
import { updateHeaderImage } from '../header/actions';
import {
    GA_HOME_FEED_CATEGORY,
    GA_LOCATION_CHANGE_ACTION,
    GA_HOME_CATEGORY,
} from '../../constants/analytics';

const {
    GA_USER_CURRENT_LOCATION_ACTION,
    GA_USER_CURRENT_LOCATION_DENIED_LABEL,
    LOCATION_STORAGE_KEY,
} = destinationConstants;

const {
    actionTypes: { UPDATE_LOCATION_SUGGESTIONS },
    actions: {
        setWaitingForLocation,
        showLocationDeniedNotification,
        updateLocation,
    },
    api: {
        getPlaceFromCoordinatesBase: getPlaceFromCoordinatesApi,
        searchLocationSuggestions: searchLocationSuggestionsApi,
    },
    constants: { LOCATION_DENIED_CODE },
} = locationDuck;

const updateLocationSuggestions = (suggestions) => ({
    type: UPDATE_LOCATION_SUGGESTIONS,
    payload: suggestions,
});

export const LOCATION_DENIED = 'LOCATION_DENIED';

const updateLocationDenied = (payload) => ({
    type: LOCATION_DENIED,
    payload,
});

export const searchLocationSuggestions = (query) => (dispatch, getState) => {
    if (!query) {
        return dispatch(updateLocationSuggestions([]));
    }

    const {
        app: { featureFlags },
    } = getState();

    return searchLocationSuggestionsApi(query).then((response) =>
        dispatch(
            updateLocationSuggestions(
                parseLocationResponse(response, featureFlags),
            ),
        ),
    );
};

const trackLocationChange = ({ newLocation, isAuthenticated }) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const {
        location: { currentPlace },
    } = state;
    const label = `${currentPlace}-${newLocation}`;

    dispatch({
        type: 'LOCATION_CHANGE',
        meta: {
            analytics: {
                category: isAuthenticated
                    ? GA_HOME_FEED_CATEGORY
                    : GA_HOME_CATEGORY,
                action: GA_LOCATION_CHANGE_ACTION,
                label,
            },
        },
    });
};

const trackUserCurrentLocation = ({ label, isAuthenticated }) => ({
    type: 'USER_CURRENT_LOCATION',
    meta: {
        analytics: {
            category: isAuthenticated
                ? GA_HOME_FEED_CATEGORY
                : GA_HOME_CATEGORY,
            action: GA_USER_CURRENT_LOCATION_ACTION,
            label,
        },
    },
});

export const locationChange = ({
    slug,
    placeId,
    latitude,
    longitude,
    bbox,
    placeType,
    currentPlace,
    currentPlaceParent,
    isOnline,
}) => (dispatch, getState) => {
    const state = getState();
    const {
        user: { isAuthenticated },
    } = state;

    const locationData = {
        slug,
        placeId,
        latitude,
        longitude,
        bbox,
        placeType,
        currentPlace,
        currentPlaceParent,
        isOnline,
    };

    let recentValue;

    /* When the user selects "online" from the Recent suggestions the placeId is null and the slug is online,
        but the isOnline is false. Because of that we should change it to reflect the correct filter */
    if (!placeId && slug === 'online') {
        locationData.isOnline = true;
    } else {
        // Make sure to explicitly set isOnline to false
        // for physical location searches, since events should
        // not be both online and have a physical location, at least for now.
        locationData.isOnline = false;
    }

    if (HAS_WINDOW) {
        if (!locationData.isOnline) {
            recentValue = placeId || `${latitude}-${longitude}`;
        }

        /*
         * Stores the current place data in the localStorage.
         * To store an object we have to replace the `,` with `;`
         * because we later reconstruct the data separating different
         * locations by `,` and the object itself should not be separated
         * until parsed as a JSON object.
         */
        storeQuery(
            JSON.stringify({
                currentPlace,
                currentPlaceParent,
                content: currentPlace,
                secondaryContent: currentPlaceParent,
                value: `recent-${recentValue}`,
                latitude,
                longitude,
                bbox,
                placeType,
                placeId,
                slug,
                isHistorySuggestion: true,
                isOnline,
            }).replace(/,/g, ';'),
            LOCATION_STORAGE_KEY,
        );
    }

    // See https://docs.google.com/document/d/1Vg-kyxM34gw71vw_5dLcfzFRhzM3scQ3FKtYUlWXLSA/edit?usp=sharing
    setLocationData(keysCamelToSnake(locationData));

    dispatch(
        trackLocationChange({
            newLocation: currentPlace,
            isAuthenticated,
        }),
    );

    dispatch(updateLocation(locationData));

    /**
     * On different cities we may want to show a different header like seasonal content
     * or specific collections with a CTA.
     */
    dispatch(updateHeaderImage());

    /**
     * We do not directly call for `fetchEventsForTab`. Updating the content of the tab is handled
     * by `feed/src/containers/BrowseContainer.js:_handleTabLoad`
     */
};

export const locationChangeWithoutSearch = ({ slug, currentPlace }) => (
    dispatch,
    getState,
) => {
    const {
        user: { isAuthenticated },
    } = getState();

    dispatch(
        trackLocationChange({
            newLocation: currentPlace,
            isAuthenticated,
        }),
    );

    dispatch(updateSearchLocationSlug(slug));
};

export const getLocationPermission = () => {
    let promise;

    if (HAS_WINDOW && window.navigator && window.navigator.permissions) {
        promise = navigator.permissions
            .query({ name: 'geolocation' })
            .then((result) => result.state);
    } else {
        promise = new Promise((_, reject) =>
            reject('No geolocation permissions object'),
        );
    }

    return promise;
};

const parseResponseFromCoordinatesApi = ({ place }) => (dispatch, getState) => {
    if (place) {
        const {
            user: { isAuthenticated },
        } = getState();

        dispatch(
            trackUserCurrentLocation({
                label: place.currentPlace,
                isAuthenticated,
            }),
        );

        return dispatch(
            locationChange({
                ...place,
                isUsingCurrentLocation: true,
            }),
        );
    }

    return dispatch(setWaitingForLocation(false));
};

/*
 * Action which enacts the browser get current location api and once that promise is resolved,
 * gets a place object from the browser, then runs a search against that data.
 */
export const getUserCurrentLocation = () => (dispatch) => {
    dispatch(setWaitingForLocation(true));

    return getBrowserLocation()
        .then(({ coords }) => {
            const { latitude, longitude } = coords;

            getPlaceFromCoordinatesApi({ latitude, longitude })
                .then(({ place }) => {
                    const _place = transformGeoPlaceObject(place);

                    dispatch(
                        parseResponseFromCoordinatesApi({ place: _place }),
                    );
                })
                .catch(() => {
                    // if we can't map the user to a city, we just resort to a rough
                    // bounding box around the current location.
                    const place = {
                        currentPlace: 'Your Location',
                        currentPlaceParent: '',
                        latitude,
                        longitude,
                        isOnline: false,
                        bbox: {
                            nw: { lng: longitude - 0.4, lat: latitude + 0.3 },
                            sw: { lng: longitude - 0.4, lat: latitude - 0.3 },
                            ne: { lng: longitude + 0.4, lat: latitude + 0.3 },
                            se: { lng: longitude + 0.4, lat: latitude - 0.3 },
                        },
                    };

                    dispatch(parseResponseFromCoordinatesApi({ place }));
                });
        })
        .catch(({ code }) => {
            if (code === LOCATION_DENIED_CODE) {
                dispatch(setWaitingForLocation(false));

                dispatch(updateLocationDenied(true));
                dispatch(showLocationDeniedNotification());

                dispatch(
                    trackUserCurrentLocation({
                        label: GA_USER_CURRENT_LOCATION_DENIED_LABEL,
                    }),
                );
            }
        });
};
